import React, { useEffect, useState, useMemo } from "react";
import AppNavBar from "../components/AppNavBar";
import { useAccountStore } from "../zustand/account";
import { useObjectStore } from "../zustand/objects";
import {
  deleteUserAndSubs,
  getUserAccount,
  getMiroSyncEntry,
  getGoogleSyncEntry,
  deleteUserMiroEntry,
  deleteUserGoogleEntry,
  getAuth0URL,
  getStripeCheckoutURL,
} from "../api";

import {
  Container,
  LoadingOverlay,
  Text,
  Button,
  Stack,
  Box,
  Paper,
  Group,
  Title,
  Anchor,
} from "@mantine/core";

import {
  editorModalShell,
  getStoryboardChromeTheme,
  storyboardCardRootStyleFromTheme,
  pageLayoutProps,
} from "../styleFile";
import { CHROME_HEADER_HEIGHT, SECTION_GAP } from "../utils/uiChromeTokens";

function accountDefaultButtonStyles(theme) {
  return {
    root: {
      backgroundColor: theme.cardBg,
      color: theme.titleColor,
      border: `1px solid ${theme.cardBorder}`,
      fontWeight: 500,
      "&:hover": {
        backgroundColor: theme.footnoteBg,
        borderColor: theme.cardEmphasisBorder,
      },
    },
  };
}

/** Narrow column inside xl container — same left gutter as Projects, readable width on large screens. */
const ACCOUNT_CONTENT_MAW = 480;

const AccountPage = () => {
  const { userStatus, checkAuthTokenValidSubscription } = useAccountStore();
  const viewportThemeOverride = useObjectStore((s) => s.viewportThemeOverride);
  const isDark = viewportThemeOverride === "dark";
  const sbTheme = useMemo(() => getStoryboardChromeTheme(isDark), [isDark]);

  const [haveEntry, setHaveEntry] = useState(false);
  const [haveGoogleEntry, setHaveGoogleEntry] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createdAt, setCreatedAt] = useState();
  const [lastLoginDate, setLastLoginDate] = useState();
  const [userEmail, setUserEmail] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.setAttribute("data-editor-theme", isDark ? "dark" : "light");
    return () => {
      document.body.removeAttribute("data-editor-theme");
    };
  }, [isDark]);

  useEffect(() => {
    getMiroSyncEntry().then((res) => {
      res === 200 && setHaveEntry(true);
    });
    getGoogleSyncEntry().then((res) => {
      res === 200 && setHaveGoogleEntry(true);
    });
    getUserAccount().then((res) => {
      setCreatedAt(new Date(res.created_at).toDateString());
      setLastLoginDate(new Date(res.lastLoggedIn).toDateString());
      setUserEmail(res.email);
      setLoading(false);
    });
  }, []);

  const deleteEntry = () => {
    deleteUserMiroEntry();
    setHaveEntry(false);
  };

  const deleteGoogleSlidesEntry = () => {
    deleteUserGoogleEntry();
    setHaveGoogleEntry(false);
  };

  const handleLoginClick = () => {
    getAuth0URL("https://mbxr.io/logging-in").then((res) => window.location.assign(res.authUrl));
  };

  const handleSubscriptionClick = (label) => {
    getStripeCheckoutURL({
      success_url: "https://mbxr.io/projects",
      cancel_url: "https://mbxr.io/logging-out",
      subscription: label,
    }).then((res) => window.location.replace(res.url));
  };

  const handleDeleteClick = () => {
    deleteUserAndSubs()
      .then(() => checkAuthTokenValidSubscription())
      .then(() => window.location.replace("https://mbxr.io/login"));
  };

  const detailRow = (label, value) => (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text size="sm" style={{ flexShrink: 0, color: sbTheme.mutedTextColor }}>
        {label}
      </Text>
      <Text size="sm" fw={500} style={{ textAlign: "right", color: sbTheme.bodyTextColor }}>
        {value}
      </Text>
    </Group>
  );

  const titleStyle = {
    color: sbTheme.headlineColor,
    fontSize: "1.35rem",
    lineHeight: 1.35,
    letterSpacing: "-0.02em",
  };

  const paperSurfaceStyle = {
    ...storyboardCardRootStyleFromTheme(sbTheme),
    boxShadow: isDark
      ? "0 1px 2px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.2)"
      : editorModalShell.styles.content.boxShadow,
  };

  const deleteConfirmPanelStyle = isDark
    ? {
        borderRadius: editorModalShell.radius,
        border: "1px solid #991b1b",
        backgroundColor: "rgba(69, 10, 10, 0.55)",
      }
    : {
        borderRadius: editorModalShell.radius,
        border: "1px solid var(--mantine-color-red-5)",
        backgroundColor: "var(--mantine-color-red-1)",
      };

  return (
    <Stack gap={0} style={{ minHeight: "100vh", width: "100%", backgroundColor: sbTheme.stackBg }}>
      <AppNavBar pageName="Account Settings" />

      <Box
        style={{
          backgroundColor: sbTheme.mainBg,
          minHeight: `calc(100vh - ${CHROME_HEADER_HEIGHT}px)`,
          width: "100%",
          boxSizing: "border-box",
          paddingBottom: 24,
        }}
      >
        <Container {...pageLayoutProps}>
          <Box style={{ position: "relative", minHeight: 200 }}>
            <LoadingOverlay
              visible={loading}
              zIndex={900}
              overlayProps={{ blur: 2, backgroundOpacity: 0.35 }}
              loaderProps={{ color: isDark ? "white" : "gray", type: "oval" }}
            />

            <Stack gap={SECTION_GAP} w="100%" maw={ACCOUNT_CONTENT_MAW} align="stretch">
              {userStatus === "loggedOut" ? (
              <Paper radius={editorModalShell.radius} withBorder={false} p="xl" style={paperSurfaceStyle}>
                <Stack gap="lg" align="flex-start">
                  <Text
                    style={{
                      ...editorModalShell.styles.title,
                      color: sbTheme.sectionLabelColor,
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Account settings
                  </Text>
                  <Title order={2} ta="left" fw={600} style={titleStyle}>
                    Sign in to manage your account
                  </Title>
                  <Button
                    w="100%"
                    size="md"
                    radius={4}
                    onClick={() => handleLoginClick()}
                    variant="default"
                    styles={accountDefaultButtonStyles(sbTheme)}
                  >
                    Log in or register
                  </Button>
                </Stack>
              </Paper>
              ) : (
              <Stack gap="xl">
                <Text
                  style={{
                    ...editorModalShell.styles.title,
                    color: sbTheme.sectionLabelColor,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Details
                </Text>

                <Paper
                  radius={editorModalShell.radius}
                  withBorder={false}
                  p={{ base: "md", sm: "lg" }}
                  style={paperSurfaceStyle}
                >
                  <Stack gap="sm">
                    {createdAt && detailRow("Created", createdAt)}
                    {userStatus === "loggedIn-active" && lastLoginDate && detailRow("Last login", lastLoginDate)}
                    {!loading && userEmail && detailRow("Email", userEmail)}
                    {userStatus !== "loggedIn-active" && (
                      <Text size="sm" style={{ color: sbTheme.mutedTextColor }}>
                        You are on the free plan.
                      </Text>
                    )}
                    <Text size="sm" style={{ color: sbTheme.mutedTextColor }}>
                      Contact{" "}
                      <Anchor
                        href="mailto:michael@matchboxxr.com"
                        size="sm"
                        fw={500}
                        underline="never"
                        style={{ color: isDark ? "#93c5fd" : "#2563eb" }}
                      >
                        michael@matchboxxr.com
                      </Anchor>{" "}
                      for support.
                    </Text>
                  </Stack>
                </Paper>

                <Text
                  style={{
                    ...editorModalShell.styles.title,
                    color: sbTheme.sectionLabelColor,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Actions
                </Text>

                <Paper
                  radius={editorModalShell.radius}
                  withBorder={false}
                  p={{ base: "md", sm: "lg" }}
                  style={paperSurfaceStyle}
                >
                  <Stack gap="md">
                    {!confirmDelete ? (
                      <Group gap="sm" wrap="wrap">
                        {haveEntry && (
                          <Button
                            size="sm"
                            radius={4}
                            onClick={() => deleteEntry()}
                            variant="default"
                            styles={accountDefaultButtonStyles(sbTheme)}
                          >
                            Unlink Miro
                          </Button>
                        )}
                        {haveGoogleEntry && (
                          <Button
                            size="sm"
                            radius={4}
                            onClick={() => deleteGoogleSlidesEntry()}
                            variant="default"
                            styles={accountDefaultButtonStyles(sbTheme)}
                          >
                            Unlink Google Slides
                          </Button>
                        )}
                        {userStatus === "loggedIn-active" ? (
                          <Button
                            size="sm"
                            radius={4}
                            component="a"
                            href="https://billing.stripe.com/p/login/6oE3eqdo4dVJf1SaEE"
                            target="_blank"
                            rel="noreferrer"
                            variant="default"
                            styles={accountDefaultButtonStyles(sbTheme)}
                          >
                            Manage paid plan
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            radius={4}
                            onClick={() => handleSubscriptionClick("pioneer")}
                            variant="default"
                            styles={accountDefaultButtonStyles(sbTheme)}
                          >
                            Upgrade
                          </Button>
                        )}
                        <Button
                          size="sm"
                          radius={4}
                          variant="light"
                          color="red"
                          styles={{
                            root: isDark
                              ? {
                                  backgroundColor: "rgba(127, 29, 29, 0.35)",
                                  color: "#fca5a5",
                                  border: "1px solid #7f1d1d",
                                  "&:hover": { backgroundColor: "rgba(127, 29, 29, 0.5)" },
                                }
                              : undefined,
                          }}
                          onClick={() => setConfirmDelete(true)}
                        >
                          Delete account
                        </Button>
                      </Group>
                    ) : (
                      <Stack gap="md" p="md" style={deleteConfirmPanelStyle}>
                        <Text size="sm" fw={500} style={{ color: isDark ? "#fca5a5" : "#c92a2a" }}>
                          Are you sure you want to delete your account?
                        </Text>
                        <Text size="xs" style={{ lineHeight: 1.5, color: isDark ? "#fecaca" : "#c92a2a" }}>
                          This will cancel any paid subscription, remove integration data from MatchboxXR, and permanently
                          delete all projects and files.
                        </Text>
                        <Group gap="sm" wrap="wrap">
                          <Button color="red" size="sm" radius={4} onClick={() => handleDeleteClick()}>
                            Yes, delete account
                          </Button>
                          <Button
                            size="sm"
                            radius={4}
                            variant="default"
                            onClick={() => setConfirmDelete(false)}
                            styles={accountDefaultButtonStyles(sbTheme)}
                          >
                            Cancel
                          </Button>
                        </Group>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Stack>
              )}
            </Stack>
          </Box>
        </Container>
      </Box>
    </Stack>
  );
};

export default AccountPage;
